import { NewRelicNoticeErrorAttributes, setNewRelicNoticeError } from "~/helpers/newrelic";
import { getSite } from "~/helpers/site";
const MAX_MESSAGE_CHARS = 500;

/** Monarch API surfaces that can fail. */
export type MonarchRequestMethod = "rule" | "ruleset" | "auto";
export interface MonarchErrorContext {
  /** Where the failure surfaced, e.g. "session-recovery" or "done-event". */
  source: string;
  /** Which Monarch surface failed: a rule request, a ruleset request, or the auto evaluation. */
  method?: MonarchRequestMethod;
  ruleId?: string;
  rulesetId?: string;
  slotId?: string;
  /** Endpoint origin the request targeted; defaults to the build's configured Monarch domain. */
  endpoint?: string;
  /** Extra one-off attributes specific to a call site. */
  extra?: NewRelicNoticeErrorAttributes;
}
export interface ParsedMonarchError {
  message: string;
  traceId?: string;
  timeoutMs?: number;
}
const truncate = (value: string, maxChars: number): string => {
  const trimmed = value.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, maxChars)}…`;
};
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

/**
 * Normalizes the error shapes the Cohesion/Monarch client emits
 * (see MonarchEvaluationsError in @redventures/cohesion-utils):
 * string | Error | { error: string, meta?: { timeout } } | { error: { error, traceId } }.
 */
export const parseMonarchError = (err: unknown): ParsedMonarchError => {
  if (err instanceof Error) return {
    message: truncate(err.message, MAX_MESSAGE_CHARS)
  };
  if (typeof err === "string") return {
    message: truncate(err, MAX_MESSAGE_CHARS)
  };
  if (err == null || typeof err !== "object") return {
    message: String(err)
  };
  const shaped = err as {
    error?: unknown;
    message?: unknown;
    meta?: {
      timeout?: unknown;
    };
    traceId?: unknown;
  };
  if (shaped.error != null && typeof shaped.error === "object") {
    const nested = shaped.error as {
      error?: unknown;
      traceId?: unknown;
    };
    const message = typeof nested.error === "string" ? nested.error : safeStringify(nested);
    const traceId = typeof nested.traceId === "string" && nested.traceId !== "" ? nested.traceId : undefined;
    return {
      message: truncate(message, MAX_MESSAGE_CHARS),
      traceId
    };
  }
  const message = typeof shaped.error === "string" ? shaped.error : typeof shaped.message === "string" ? shaped.message : safeStringify(err);
  const traceId = typeof shaped.traceId === "string" && shaped.traceId !== "" ? shaped.traceId : undefined;
  const timeoutMs = typeof shaped.meta?.timeout === "number" ? shaped.meta.timeout : undefined;
  return {
    message: truncate(message, MAX_MESSAGE_CHARS),
    traceId,
    timeoutMs
  };
};
const getDefaultMonarchEndpoint = (): string | undefined => {
  if (process.env.allconnect === "true") return process.env.acMonarchDomain;
  if (getSite().isFRIN) {
    return process.env.frinMonarchDomain || process.env.lpMonarchDomain || "https://monarch.cohesionapps.com";
  }
  return process.env.lpMonarchDomain || "https://monarch.cohesionapps.com";
};

/**
 * Reports a Monarch failure to New Relic with structured, queryable attributes
 * (source, API surface, rule/ruleset/slot id, endpoint, traceId, timeout) instead
 * of the stringified blob emitted by the bricks monitoring. No-op for nullish errors.
 */
export const reportMonarchError = (err: unknown, context: MonarchErrorContext): void => {
  if (err == null) return;
  const {
    message,
    traceId,
    timeoutMs
  } = parseMonarchError(err);
  const endpoint = context.endpoint ?? getDefaultMonarchEndpoint();
  const attributes: NewRelicNoticeErrorAttributes = {
    ...context.extra,
    monarch: "true",
    monarchSource: context.source
  };
  if (context.method) attributes.monarchMethod = context.method;
  if (context.ruleId) attributes.monarchRuleId = context.ruleId;
  if (context.rulesetId) attributes.monarchRulesetId = context.rulesetId;
  if (context.slotId) attributes.monarchSlotId = context.slotId;
  if (endpoint) attributes.monarchEndpoint = endpoint;
  if (traceId) attributes.monarchTraceId = traceId;
  if (timeoutMs !== undefined) attributes.monarchTimeoutMs = timeoutMs;
  setNewRelicNoticeError(new Error(`monarch:${context.source} ${message}`), attributes);
};